import type { Store } from '@reduxjs/toolkit';

import type { RootState } from './rootReducer';
import type { AppDispatch } from './store';

/**
 * Late-bound access to the Redux store for code that is neither a React component nor a saga:
 * the non-hook getters in lib/hooks/useMfaChallenge.ts, the lib/stateLayer bridges, the
 * crossLayer selectors, lib/utils/utm.ts.
 *
 * Those modules MUST NOT import `@lib/redux/store`: it runs `sagaMiddleware.run(rootSaga)` at
 * import, and cartSaga → useMfaChallenge → (redux twin) → store → rootSaga → cartSaga would
 * evaluate rootSaga with an undefined cartSaga. `store.ts` registers itself here instead
 * (lint-enforced: `@lib/redux/store` is importable only from the two app roots, the boot and the
 * persistence layer).
 */
export type AppStore = Store<RootState> & { dispatch: AppDispatch };

let registered: AppStore | undefined;

export const registerReduxStore = (store: AppStore): void => {
    registered = store;
};

export const getReduxStore = (): AppStore => {
    if (!registered) {
        throw new Error('[lib/redux/access] Redux store not registered yet');
    }
    return registered;
};

export const getReduxState = (): RootState => getReduxStore().getState();

export const getReduxDispatch = (): AppDispatch => getReduxStore().dispatch;

/** Tests only: a fresh `createAppStore()` per test re-registers itself. */
export const resetReduxStoreForTests = (): void => {
    registered = undefined;
};
